/**
 * DiscogsService - searches Discogs for releases/tracks and normalizes metadata.
 * Requires a token (user token or consumer token). We read from REACT_APP_DISCOGS_TOKEN.
 * Docs: https://www.discogs.com/developers/
 */

const DISCOGS_API_BASE = 'https://api.discogs.com';
const DISCOGS_TOKEN = process.env.REACT_APP_DISCOGS_TOKEN || 'lsdokTIIWROMMpUwPrVJWMvpbMOmcNOpBuakqmtg'; // TODO: move token to .env

const roles = {
	composer: ['composed by', 'composer', 'written-by', 'music by'],
	lyricist: ['lyrics by', 'lyricist', 'words by'],
	arranger: ['arranged by', 'orchestrated by', 'orchestrator'],
};

/**
 * Discogs decorates artist names with two catalogue conventions that are
 * meaningless outside Discogs and read as typos to a parent:
 *
 *   "Justin Paul (5)"  — a numeric suffix disambiguating same-named artists
 *   "Original Cast*"   — an asterisk marking an artist NAME VARIATION (ANV)
 *
 * Both were invisible while these names were only ever fetched and discarded.
 * They became user-facing the moment credits started rendering in the song
 * meta line and the print-out, so they are stripped at the boundary rather
 * than at each display site — there is more than one display site, and a
 * second one added later would not know to do it.
 *
 * Stripped BEFORE the Set, so "Justin Paul (5)" and "Justin Paul" arriving
 * from two different credit rows collapse to one name instead of both showing.
 */
const cleanDiscogsName = (name = '') =>
	name
		.replace(/\s*\(\d+\)\s*$/, '')
		.replace(/\*+$/, '')
		.trim();

const normalizePeople = (extraArtists = [], matchRoles = []) => {
	const names = extraArtists
		.filter((a) => a?.role && matchRoles.some((r) => a.role.toLowerCase().includes(r)))
		.map((a) => cleanDiscogsName(a.name))
		.filter(Boolean);
	return Array.from(new Set(names));
};

/**
 * A list-grade option built from a SEARCH result — no follow-up request.
 *
 * Same finding as MusicService's buildOptionFromSearchResult: this service used
 * to fetch the full release detail for EVERY result (measured: 13 requests for
 * one search), when the search payload already carries what a row renders.
 * Discogs formats `title` as "Artist - Album", so both fall out of one split.
 *
 * `extraartists` — the composer/lyricist credits — is detail-only, so these
 * rows are credit-free and marked `detailsFetched: false`. `resourceUrl` is
 * carried forward so `fetchDiscogsReleaseDetail` can hydrate on pick.
 */
export const buildDiscogsOptionFromSearchResult = (r = {}, fallbackTitle = '', fallbackArtist = '') => {
	const raw = String(r.title || '');
	const sep = raw.indexOf(' - ');
	const artist = sep > -1 ? raw.slice(0, sep).trim() : fallbackArtist;
	const album = sep > -1 ? raw.slice(sep + 3).trim() : raw || fallbackTitle;
	const genres = r.genre || [];
	const styles = r.style || [];

	return {
		title: album || fallbackTitle,
		artist: artist || fallbackArtist || '',
		album,
		releaseYear: /^\d{4}$/.test(String(r.year || '')) ? Number(r.year) : null,
		genres,
		tags: styles,
		recordingType: (r.format || []).join(' '),
		source: 'discogs',
		musicbrainzId: null,
		recordingId: `discogs-${r.id ?? ''}`,
		previewUrl: '',
		artworkUrl: r.cover_image || r.thumb || '',
		composers: [],
		lyricists: [],
		arrangers: [],
		isMusical:
			genres.some((g) => String(g).toLowerCase().includes('stage & screen')) ||
			styles.some((s) => String(s).toLowerCase().includes('musical')),
		musicalName: '',
		yearWritten: null,
		releaseLabel: (r.label || [])[0] || '',
		releaseDisambiguation: r.country || '',
		// Kept so the pick-time hydrator can fetch this exact release.
		resourceUrl: r.resource_url || '',
		detailsFetched: false,
	};
};

export const fetchReleaseDetail = async (resourceUrl) => {
	const res = await fetch(resourceUrl, {
		headers: {
			Authorization: `Discogs token=${DISCOGS_TOKEN}`,
			'User-Agent': 'CYVWebsite/1.0',
		},
	});
	if (!res.ok) return null;
	return res.json();
};

export const searchDiscogsOptions = async (title, artist = '', limit = 8) => {
	if (!DISCOGS_TOKEN) {
		console.warn('Discogs token not set; skipping Discogs search');
		return [];
	}

	try {
		const params = new URLSearchParams({
			q: `${title} ${artist}`.trim(),
			type: 'release',
			per_page: String(limit),
			token: DISCOGS_TOKEN,
		});

		const res = await fetch(`${DISCOGS_API_BASE}/database/search?${params.toString()}`, {
			headers: { 'User-Agent': 'CYVWebsite/1.0' },
		});
		if (!res.ok) throw new Error(`Discogs search error: ${res.status}`);
		const data = await res.json();
		if (!data.results || data.results.length === 0) return [];

		// ONE request, not one-per-result. The per-release detail fetch that used
		// to run here is deferred to pick time (hydrateDiscogsOption).
		return data.results
			.slice(0, limit)
			.map((r) => buildDiscogsOptionFromSearchResult(r, title, artist));
	} catch (err) {
		console.error('Error fetching Discogs options:', err);
		return [];
	}
};

// Simple rate limit (1 req/sec)
let lastRequestTime = 0;
const RATE_LIMIT_MS = 1000;
const rateLimit = () => {
	return new Promise((resolve) => {
		const now = Date.now();
		const elapsed = now - lastRequestTime;
		if (elapsed < RATE_LIMIT_MS) {
			setTimeout(() => {
				lastRequestTime = Date.now();
				resolve();
			}, RATE_LIMIT_MS - elapsed);
		} else {
			lastRequestTime = now;
			resolve();
		}
	});
};

export const searchDiscogsOptionsWithRateLimit = async (...args) => {
	await rateLimit();
	return searchDiscogsOptions(...args);
};

/**
 * Pick-time hydration for ONE chosen Discogs row — the credits (`extraartists`)
 * that only the release detail carries. Returns the option unchanged if there
 * is nothing to fetch or the fetch fails, so a picked song is never lost to a
 * flaky enrichment call.
 */
export const hydrateDiscogsOption = async (option) => {
	if (!option?.resourceUrl || option.detailsFetched) return option;
	await rateLimit();
	let detail = null;
	try {
		detail = await fetchReleaseDetail(option.resourceUrl);
	} catch (err) {
		console.warn('Discogs detail fetch failed:', err.message);
	}
	if (!detail) return option;

	return {
		...option,
		album: detail.title || option.album,
		artist: detail.artists?.map((a) => a.name).join(', ') || option.artist,
		releaseYear: detail.year || option.releaseYear,
		genres: detail.genres || option.genres,
		tags: detail.styles || option.tags,
		recordingType:
			(detail.formats?.[0]?.descriptions || []).join(' ') || option.recordingType,
		composers: normalizePeople(detail.extraartists, roles.composer),
		lyricists: normalizePeople(detail.extraartists, roles.lyricist),
		arrangers: normalizePeople(detail.extraartists, roles.arranger),
		releaseLabel: detail.labels?.[0]?.name || option.releaseLabel,
		releaseDisambiguation: detail.country || option.releaseDisambiguation,
		detailsFetched: true,
	};
};

export default {
	searchDiscogsOptions,
	searchDiscogsOptionsWithRateLimit,
	hydrateDiscogsOption,
};
